// src/components/ActualizacionBanner.jsx
// Barra "Hay una versión nueva — Actualizar". La app guarda una copia de sí misma para abrir rápido
// (service worker) y por eso, tras un deploy, enseñaba la versión vieja hasta abrirla dos veces.
// Aquí se revisa sola si el servidor ya tiene otra versión: al abrir, al volver a la app, al
// recuperar la conexión y cada 10 minutos. Al tocar "Actualizar" se borra la copia vieja del
// index.html y se recarga.
import React, { useState, useEffect, useRef } from 'react';
import { hayVersionNueva, CADA_MS } from '../services/actualizacion';

const srcActual = () => document.querySelector('script[src*="/static/js/main."]')?.src || '';

export default function ActualizacionBanner() {
  const [hay, setHay] = useState(false);
  const [ocultaHasta, setOcultaHasta] = useState(0);
  const revisando = useRef(false);

  useEffect(() => {
    const actual = srcActual();
    if (!actual) return undefined; // en desarrollo (npm start) no hay main.<hash>.js
    const revisar = async () => {
      if (revisando.current || document.visibilityState === 'hidden' || navigator.onLine === false) return;
      revisando.current = true;
      try {
        // "_ver" hace que el service worker deje pasar la petición y no sirva la copia guardada.
        const res = await fetch(`/index.html?_ver=${Date.now()}`, { cache: 'no-store' });
        if (res.ok && hayVersionNueva(await res.text(), actual)) setHay(true);
      } catch { /* sin señal: se vuelve a intentar luego */ }
      revisando.current = false;
    };
    const t0 = setTimeout(revisar, 8000);
    const iv = setInterval(revisar, CADA_MS);
    const alVolver = () => { if (document.visibilityState === 'visible') revisar(); };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('online', revisar);
    return () => {
      clearTimeout(t0); clearInterval(iv);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('online', revisar);
    };
  }, []);

  const actualizar = async () => {
    try {
      const claves = await caches.keys();
      await Promise.all(claves.filter((k) => k.startsWith('click-travel-shell-')).map(async (k) => {
        const c = await caches.open(k);
        await c.delete('/index.html');
        await c.delete('/');
      }));
    } catch { /* si no se puede, la recarga igual trae lo nuevo en la siguiente apertura */ }
    window.location.reload();
  };

  if (!hay || Date.now() < ocultaHasta) return null;
  return (
    <div role="status" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000, background: 'var(--orange, #FF6B00)', color: '#fff',
      padding: 'calc(env(safe-area-inset-top, 0px) + 8px) 12px 8px', display: 'flex', alignItems: 'center', gap: 10,
      fontSize: 13, fontWeight: 600, boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
    }}>
      <span style={{ flex: 1, lineHeight: 1.3 }}>Hay una versión nueva de la app.</span>
      <button type="button" onClick={actualizar} style={{ background: '#fff', color: 'var(--orange, #FF6B00)', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 800 }}>Actualizar</button>
      <button type="button" aria-label="Ahora no" onClick={() => setOcultaHasta(Date.now() + 30 * 60 * 1000)} style={{ background: 'transparent', color: '#fff', border: 'none', fontSize: 18, lineHeight: 1, padding: '4px 6px' }}>×</button>
    </div>
  );
}
